<template>
  <v-app id="inspire"
         class="ContentLayout">
    <ImpersonatingSystemBar>
    </ImpersonatingSystemBar>
    <content-drawer v-if="useDrawer && authenticated && user">
    </content-drawer>
    <v-app-bar app
               :absolute="inFrame"
               :color="$root.appBarColor"
               flat
               tile
               :dark="$root.appBarDark">
      <v-app-bar-nav-icon v-if="useDrawer && authenticated && user"
                          @click.stop="drawer = !drawer">
      </v-app-bar-nav-icon>
      <router-link :to="{name: 'library.home'}"
                   v-if="!drawer"
                   class="mr-4">
        <v-img :src="$root.appBarLogo"
               max-width="240"
               max-height="48"
               contain
               alt="Mobile Locker logo">
        </v-img>
      </router-link>
      <search-form v-if="canUseSearch"
                   class="mr-2">
      </search-form>
      <v-spacer v-else></v-spacer>

      <HaystackDialog>
      </HaystackDialog>

      <CustomerPickerDialog>
      </CustomerPickerDialog>

      <v-btn v-if="isPresentationHTML"
             @click.prevent="makeFullScreen"
             class="mr-2"
             icon>
        <v-icon>
          fas fa-expand
        </v-icon>
      </v-btn>
      <v-btn v-if="isPresentingContent"
             @click.prevent="toggleBookmark"
             :title="isBookmarked ? 'Remove from favorites' : 'Add to favorites'"
             dark
             icon
             class="mr-2">
        <v-icon v-if="bookmarkBusy">
          fal fa-spin fa-spinner
        </v-icon>
        <v-icon v-else-if="isBookmarked">
          fas fa-bookmark
        </v-icon>
        <v-icon v-else>
          fal fa-bookmark
        </v-icon>
      </v-btn>
      <v-btn v-if="isPresentingContent && isReturnToCRM"
             color="red lighten-2"
             dark
             @click.prevent="returnToCRM">
        Return to {{ crmLabel }}
      </v-btn>
      <v-btn v-else-if="!isContentLibrary"
             color="red lighten-2"
             dark
             @click.prevent="returnToContentLibrary">
        Content Library
      </v-btn>
      <v-switch :label="liveMode ? 'Live Mode' : 'Test Mode'"
                color="red"
                v-model="liveMode"
                hide-details
                class="ml-2"
                :disabled="liveModeToggleDisabled">
      </v-switch>

    </v-app-bar>
    <v-main class="Layout__Main">
      <transition name="page"
                  mode="out-in">
        <router-view></router-view>
      </transition>
    </v-main>
    <error-snackbar>
    </error-snackbar>
    <success-snackbar>
    </success-snackbar>
    <ReloadRequiredDialog>
    </ReloadRequiredDialog>
  </v-app>
</template>

<script>
import {mapGetters} from 'vuex'
import moment from 'moment-timezone';
import NavigationDrawer from '../components/NavigationDrawer';
import PageFooter from '../components/PageFooter';
import ErrorSnackbar from '../components/ErrorSnackbar';
import SuccessSnackbar from '../components/SuccessSnackbar';
import ContentDrawer from '../pages/content/components/ContentDrawer';
import ContentLibraryDialog from '../pages/content/components/ContentLibraryDialog';
import SearchForm from "../components/SearchForm";
import DOMPurify from 'dompurify';
import CustomerPickerDialog from '../pages/crm/components/CustomerPickerDialog.vue';
import ReloadRequiredDialog from '../components/ReloadRequiredDialog.vue';
import HaystackDialog from '../pages/ai/components/HaystackDialog.vue';
import ImpersonatingSystemBar from '../components/ImpersonatingSystemBar.vue';

export default {
  components: {
    ImpersonatingSystemBar,
    HaystackDialog,
    ReloadRequiredDialog,
    ContentLibraryDialog,
    ContentDrawer,
    CustomerPickerDialog, // MLD-5823
    SuccessSnackbar,
    ErrorSnackbar,
    PageFooter,
    NavigationDrawer,
    SearchForm,
  },
  data() {
    return {
      useDrawer: true,
      // Using null as the starting value for its v-model will initialize the drawer as closed on mobile and as open on desktop.
      year: moment().format('YYYY'),
    }
  },
  computed: {
    ...mapGetters([
      'authenticated',
      'errorMessage',
      'crmLabel', // MLD-2598
      'isAdmin',
      'isEmployee',
      'isMember',
      'isSuperAdmin',
      'presentations',
      'reloadRequired', // MLD-4934
      'team',
      'title',
      'user',
    ]),
    ...mapGetters('content', [
      'bookmarkBusy', // MLD-5591
      'isPresentationBookmarked', // MLD-5591
      'presentation',
      'session',
      'theme',
    ]),
    ...mapGetters('meta', [
      'meta',
    ]),
    isPresentationHTML() { // MLD-3218
      if (this.$route.name === 'library.present' && this.presentation && this.presentation.type_html) {
        return true;
      }
      return false;
    },
    liveModeToggleDisabled() { // MLD-3086
      if (this.session && this.session.presentation && this.session.presentation.is_internal_use_only) {
        return true;
      }
      return false;
    },
    canUseSearch() { // MLD-2483
      return this.user
        && this.user.is_full_user
        && this.team
        && this.team.search_enabled
        && this.team.show_search_in_website;
    },
    drawer: {
      get() {
        return this.$store.state.system.drawer;
      },
      set(val) {
        this.$store.commit('setDrawer', val)
      },
    },
    liveMode: {
      get() {
        return this.$store.state.content.liveMode;
      },
      set(val) {
        this.$store.dispatch('content/setLiveMode', val);
      }
    },
    inFrame() {
      return this.$route.query.iframe === 'salesforce';
    },
    isBookmarked() { // MLD-5591
      return this.isPresentingContent
        && this.isPresentationBookmarked;
    },
    isContentLibrary() {
      return this.$route.name === 'library.home';
    },
    isPresentingContent() {
      return this.$route.name === 'library.present';
    },
    isReturnToCRM() {
      try {
        return parseInt(this.$route.query.return_to_crm) === 1;
      } catch (e) {
        return false;
      }
    },
  },
  created() {
    //this.initVisibilityChecking(); // MLD-4934
  },
  methods: {
    async logout() {
      await this.$store.dispatch('logout');
    },
    async returnToCRM() { // MLD-2463
      if (!confirm(`Are you sure you want to end this session and return to ${DOMPurify.sanitize(this.crmLabel)}?`)) {
        return;
      }
      if (this.session) {
        await this.$store.dispatch('meta/updateMetadata');
        const payload = {
          session: this.session,
          meta: this.meta,
        }
        await this.$store.dispatch('content/endSession', payload);
        await this.$store.dispatch('meta/unloadDocument');
      }
      window.close();
    },
    async returnToContentLibrary() { // MLD-2811
      this.$store.dispatch('openDrawer').then(); // MLD-5641
      await this.$router.push({name: 'library.home'});
    },
    makeFullScreen() { // MLD-3218
      // @see https://www.w3schools.com/howto/howto_js_fullscreen.asp
      const elem = document.getElementById('ml_presentation_iframe');
      if (elem) {
        if (elem.requestFullscreen) {
          elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { /* Safari */
          elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { /* IE11 */
          elem.msRequestFullscreen();
        }
      }
    },
    async toggleBookmark() { // MLD-2107
      if (this.presentation) {
        await this.$store.dispatch('content/toggleBookmark', this.presentation.id);
        if (this.isBookmarked) {
          this.$toast.success('Added to favorites.');
        } else {
          this.$toast.success('Removed from favorites.');
        }
      }
    },
  }
}
</script>
